import { FC, useCallback, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DownloadIcon from '@mui/icons-material/Download'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import VisibilityIcon from '@mui/icons-material/Visibility'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { useSnackbar } from 'notistack'
import { documentoService } from '@/services/documentoService'
import { Documento } from '@/types/documento'
import { ChecklistReserva, ItemChecklistReserva } from '@/types/estruturacao'
import { formatDateTime, formatFileSize } from '@/utils/formatters'
import {
  chaveDoGrupo,
  ehSolicitacaoCuradoria,
  gruposComItens,
  rotuloDoGrupo,
  rotuloDoItem,
  statusDoItem,
} from '@/utils/checklistReserva'
import { ChipStatusItem, DetalhesItemChecklist } from '@/components/documentos/ItemChecklistInfo'

/**
 * Checklist da reserva com os ARQUIVOS dentro de cada item (relato de teste do usuário,
 * 14/10/2026): "o documento destinado ao Balanço Patrimonial deveria aparecer anexado nesse
 * destino, não numa lista única". O arquivo vai para o item pela `exigenciaChave` gravada no
 * upload; arquivo antigo sem chave cai no item de mesmo tipo quando só UM item tem aquele tipo
 * (sem ambiguidade) e o tipo não é o coringa `OUTRO`; o resto fica em "Outros arquivos". Usado
 * na Validação de Reservas e na Estruturação do back-office.
 */

export interface ArquivosAgrupados {
  /** Arquivos por chave do item (`item.chave`). */
  porItem: Map<string, Documento[]>
  /** Arquivos que não se associam a nenhum item. */
  outros: Documento[]
}

const chaveDoItem = (item: ItemChecklistReserva): string => item.chave ?? `rotulo:${item.label}`

export const agruparArquivosPorItem = (
  checklist: ChecklistReserva | null | undefined,
  documentos: Documento[]
): ArquivosAgrupados => {
  const itens = (checklist?.categorias ?? []).flatMap((g) => g.itens)
  const porItem = new Map<string, Documento[]>()
  const outros: Documento[] = []
  const chavesValidas = new Set(itens.map((i) => i.chave).filter((c): c is string => !!c))
  const add = (k: string, d: Documento) => porItem.set(k, [...(porItem.get(k) ?? []), d])
  // P3: o checklist já diz quais arquivos ATIVOS são de cada item (`documentos[].documentoId`).
  const itemDoDocumento = new Map<string, string>()
  for (const i of itens) {
    for (const ativo of i.documentos ?? []) itemDoDocumento.set(ativo.documentoId, chaveDoItem(i))
  }

  for (const d of documentos) {
    const doChecklist = itemDoDocumento.get(d.id)
    if (doChecklist) {
      add(doChecklist, d)
      continue
    }
    if (d.exigenciaChave && chavesValidas.has(d.exigenciaChave)) {
      add(d.exigenciaChave, d)
      continue
    }
    // `OUTRO` é o tipo coringa do upload genérico: não diz a que item o arquivo se destina
    if (!d.exigenciaChave && d.tipo !== 'OUTRO') {
      const mesmoTipo = itens.filter((i) => i.tipo && i.tipo === d.tipo)
      if (mesmoTipo.length === 1) {
        add(chaveDoItem(mesmoTipo[0]), d)
        continue
      }
    }
    outros.push(d)
  }
  return { porItem, outros }
}

// ─── Abrir / baixar / pré-visualizar ─────────────────────────────────────────

export interface PreviewArquivo {
  doc: Documento
  url: string
}

/**
 * Abre o arquivo pelo backend (`/arquivo`, stream — nunca o link interno do MinIO): PDF e
 * imagem abrem na pré-visualização; o resto (ou "Baixar") é salvo por âncora, sem pop-up.
 */
export const useArquivoReserva = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [preview, setPreview] = useState<PreviewArquivo | null>(null)
  const [ocupadoId, setOcupadoId] = useState<string | null>(null)

  const abrirArquivo = useCallback(
    async (doc: Documento, download: boolean) => {
      setOcupadoId(doc.id)
      try {
        const blob = await documentoService.baixarArquivo(doc.id)
        const url = URL.createObjectURL(blob)
        const visualizavel = /^(application\/pdf|image\/)/.test(doc.mimeType ?? '')
        if (!download && visualizavel) {
          setPreview({ doc, url })
          return
        }
        const a = document.createElement('a')
        a.href = url
        a.download = doc.nome
        document.body.appendChild(a)
        a.click()
        a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 60_000)
      } catch (e) {
        enqueueSnackbar((e as { message?: string })?.message || 'Falha ao abrir o documento', {
          variant: 'error',
        })
      } finally {
        setOcupadoId(null)
      }
    },
    [enqueueSnackbar]
  )

  const fecharPreview = useCallback(() => {
    setPreview((p) => {
      if (p) URL.revokeObjectURL(p.url)
      return null
    })
  }, [])

  return { abrirArquivo, preview, fecharPreview, ocupadoId }
}

export const ArquivoPreviewDialog: FC<{
  preview: PreviewArquivo | null
  onFechar: () => void
  onBaixar: (doc: Documento) => void
}> = ({ preview, onFechar, onBaixar }) => (
  <Dialog open={preview != null} onClose={onFechar} maxWidth="lg" fullWidth>
    {preview && (
      <>
        <DialogTitle sx={{ fontWeight: 700 }}>{preview.doc.nome}</DialogTitle>
        <DialogContent sx={{ height: '75vh', p: 0 }}>
          {preview.doc.mimeType?.startsWith('image/') ? (
            <Box
              component="img"
              src={preview.url}
              alt={preview.doc.nome}
              sx={{ maxWidth: '100%', maxHeight: '100%', display: 'block', mx: 'auto' }}
            />
          ) : (
            <Box component="iframe" title={preview.doc.nome} src={preview.url} sx={{ width: '100%', height: '100%', border: 0 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onBaixar(preview.doc)} startIcon={<DownloadIcon />}>
            Baixar
          </Button>
          <Button onClick={onFechar}>Fechar</Button>
        </DialogActions>
      </>
    )}
  </Dialog>
)

// ─── Linha de arquivo ────────────────────────────────────────────────────────

const ArquivoLinha: FC<{
  doc: Documento
  ocupado: boolean
  onAbrir: (doc: Documento, download: boolean) => void
}> = ({ doc, ocupado, onAbrir }) => (
  <ListItem
    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, mb: 0.75, pr: 11 }}
    secondaryAction={
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Visualizar">
          <span>
            <IconButton size="small" aria-label={`Visualizar ${doc.nome}`} disabled={ocupado} onClick={() => onAbrir(doc, false)}>
              {ocupado ? <CircularProgress size={16} /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Baixar">
          <span>
            <IconButton size="small" aria-label={`Baixar ${doc.nome}`} disabled={ocupado} onClick={() => onAbrir(doc, true)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    }
  >
    <ListItemIcon sx={{ minWidth: 32 }}>
      <InsertDriveFileIcon fontSize="small" color={doc.tipoDivergente ? 'warning' : 'action'} />
    </ListItemIcon>
    <ListItemText
      primary={doc.nome}
      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600, noWrap: true }}
      secondary={[
        doc.tipo,
        formatFileSize(doc.tamanhoBytes),
        formatDateTime(doc.dataUpload ?? doc.createdAt),
        doc.status,
        doc.tipoDivergente && doc.tipoIdentificadoIa ? `IA identificou: ${doc.tipoIdentificadoIa}` : null,
      ]
        .filter(Boolean)
        .join(' · ')}
      secondaryTypographyProps={{ fontSize: '0.75rem' }}
    />
  </ListItem>
)

// ─── Checklist com arquivos ──────────────────────────────────────────────────

/**
 * Ações do back-office por item (Validação da reserva, AN-PP3 §7.1). Sem `acoes`, o componente é
 * só leitura (Estruturação do back-office, diálogo de documentos).
 */
export interface AcoesItemChecklist {
  onValidar: (item: ItemChecklistReserva) => void
  onRecusar: (item: ItemChecklistReserva) => void
  /**
   * Reserva na curadoria (Jornada ADM 30/10): "Devolver" em cada item no lugar de Recusar/Invalidar
   * — recusa o arquivo, se houver, e põe a reserva em pendência.
   */
  onDevolver?: (item: ItemChecklistReserva) => void
  onHistorico: (item: ItemChecklistReserva) => void
  /** Falso = botões desabilitados (ex.: reserva fora da janela de validação). */
  habilitadas?: boolean
}

/** Validar só faz sentido com arquivo processado ainda não validado (o backend decide o resto). */
const podeValidar = (item: ItemChecklistReserva) => {
  const s = statusDoItem(item)
  return (s === 'RECEBIDO' || s === 'RECUSADO' || s === 'VENCIDO') && (item.documentos ?? []).length > 0
}

/** Recusar vale em RECEBIDO, VALIDADO (= invalidar) e VENCIDO. */
const podeRecusar = (item: ItemChecklistReserva) => {
  const s = statusDoItem(item)
  return s === 'RECEBIDO' || s === 'VALIDADO' || s === 'VENCIDO'
}

/** Devolver vale em qualquer item com chave, menos com arquivo ainda em verificação. */
const podeDevolver = (item: ItemChecklistReserva) => !!item.chave && statusDoItem(item) !== 'QUARENTENA'

export const ChecklistComArquivos: FC<{
  checklist: ChecklistReserva | null
  documentos: Documento[]
  ocupadoId?: string | null
  onAbrir: (doc: Documento, download: boolean) => void
  /** Back-office: mostra quem decidiu (o consultor nunca vê o autor). */
  mostrarAutor?: boolean
  acoes?: AcoesItemChecklist
}> = ({ checklist, documentos, ocupadoId, onAbrir, mostrarAutor = false, acoes }) => {
  const { porItem, outros } = agruparArquivosPorItem(checklist, documentos)
  const grupos = gruposComItens(checklist)
  const temItens = grupos.length > 0
  const habilitadas = acoes?.habilitadas !== false

  return (
    <Box>
      {temItens &&
        grupos.map((grupo, indice) => {
          return (
            <Box key={chaveDoGrupo(grupo, indice)} sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                component="div"
                sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em', mb: 0.5 }}
              >
                {rotuloDoGrupo(grupo)}
              </Typography>
              {grupo.itens.map((item) => {
                const arquivos = porItem.get(chaveDoItem(item)) ?? []
                const status = statusDoItem(item)
                const rotulo = rotuloDoItem(item)
                return (
                  <Box key={chaveDoItem(item)} sx={{ mb: 1.5 }} aria-label={`Item ${rotulo}`} role="group">
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                      {status === 'VALIDADO' ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : item.obrigatorio && status !== 'RECEBIDO' ? (
                        <WarningAmberIcon color="warning" fontSize="small" />
                      ) : (
                        <InsertDriveFileIcon color="disabled" fontSize="small" />
                      )}
                      <Typography variant="body2" fontWeight={600}>
                        {rotulo}
                      </Typography>
                      <ChipStatusItem status={status} />
                      {!item.obrigatorio && <Chip size="small" variant="outlined" label="Opcional" />}
                      {ehSolicitacaoCuradoria(item) && (
                        <Chip size="small" variant="outlined" color="secondary" label="Solicitado pela Curadoria" />
                      )}
                      {item.validadeDias != null && (
                        <Typography variant="caption" color="text.secondary">
                          Validade: {item.validadeDias} dias da emissão
                        </Typography>
                      )}
                      {acoes && (
                        <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
                          {podeValidar(item) && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={!habilitadas}
                              onClick={() => acoes.onValidar(item)}
                              aria-label={`Validar ${rotulo}`}
                            >
                              Validar
                            </Button>
                          )}
                          {acoes.onDevolver && podeDevolver(item) && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled={!habilitadas}
                              onClick={() => acoes.onDevolver?.(item)}
                              aria-label={`Devolver ${rotulo} ao consultor`}
                            >
                              Devolver
                            </Button>
                          )}
                          {!acoes.onDevolver && podeRecusar(item) && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={!habilitadas}
                              onClick={() => acoes.onRecusar(item)}
                              aria-label={`${status === 'VALIDADO' ? 'Invalidar' : 'Recusar'} ${rotulo}`}
                            >
                              {status === 'VALIDADO' ? 'Invalidar' : 'Recusar'}
                            </Button>
                          )}
                          {item.chave && (
                            <Button size="small" onClick={() => acoes.onHistorico(item)} aria-label={`Histórico de ${rotulo}`}>
                              Histórico
                            </Button>
                          )}
                        </Stack>
                      )}
                    </Stack>
                    <Box sx={{ pl: 3.5 }}>
                      <DetalhesItemChecklist item={item} mostrarAutor={mostrarAutor} />
                    </Box>
                    {arquivos.length > 0 ? (
                      <List dense disablePadding sx={{ pl: 3.5 }}>
                        {arquivos.map((d) => (
                          <ArquivoLinha key={d.id} doc={d} ocupado={ocupadoId === d.id} onAbrir={onAbrir} />
                        ))}
                      </List>
                    ) : item.presente ? (
                      <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5, display: 'block' }}>
                        Entregue — o arquivo está em &quot;Outros arquivos&quot;.
                      </Typography>
                    ) : null}
                  </Box>
                )
              })}
            </Box>
          )
        })}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {temItens ? `Outros arquivos (${outros.length})` : `Arquivos enviados (${documentos.length})`}
      </Typography>
      {(temItens ? outros : documentos).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {temItens ? 'Nenhum arquivo fora do checklist.' : 'Nenhum arquivo enviado nesta reserva.'}
        </Typography>
      ) : (
        <List dense disablePadding aria-label={temItens ? 'Outros arquivos' : 'Arquivos enviados'}>
          {(temItens ? outros : documentos).map((d) => (
            <ArquivoLinha key={d.id} doc={d} ocupado={ocupadoId === d.id} onAbrir={onAbrir} />
          ))}
        </List>
      )}
    </Box>
  )
}
